import { normalizeMethod, schemaExampleFromJsonSchema } from './normalize';
import { defaultCollectionAuth, defaultRequestAuth } from '../requestAuth';
import { generateFolderId } from '../collectionFolders';

const METHODS = ['get', 'post', 'put', 'patch', 'delete', 'head', 'options'];

const MAX_NAME = 80;
function requestName(operation, method, path) {
  const summary = String(operation.summary || '').trim();
  if (summary && summary.length <= MAX_NAME && !/[\r\n]/.test(summary)) return summary;
  return operation.operationId || `${method.toUpperCase()} ${path}`;
}

/**
 * Make the names unique within one collection.
 *
 * `summary` reads better than `operationId` and is what a person wrote, but the
 * spec only requires `operationId` to be unique — "Get user", "List" and
 * "Delete" repeat freely, and a versioned API (`/users/{id}` and
 * `/v2/users/{id}`) will happily use the same summary for both. Imported as-is
 * that produces a sidebar of identical rows with no way to tell them apart,
 * which is worse than the uglier name.
 *
 * So: a name used once is left exactly as it was. A name used more than once
 * gains its path — and, if that still collides (same summary, same path,
 * different verbs), its method too.
 *
 * @param {Array<{name: string}>} requests mutated in place
 * @param {Array<{method: string, path: string}>} origins parallel to `requests`
 */
function disambiguateNames(requests, origins) {
  const counts = new Map();
  requests.forEach((r) => counts.set(r.name, (counts.get(r.name) || 0) + 1));

  const withPath = new Map();
  requests.forEach((r, i) => {
    if (counts.get(r.name) < 2) return;
    const name = `${r.name} · ${origins[i].path}`;
    withPath.set(name, (withPath.get(name) || 0) + 1);
  });

  requests.forEach((r, i) => {
    if (counts.get(r.name) < 2) return;
    const { method, path } = origins[i];
    const name = `${r.name} · ${path}`;
    r.name = withPath.get(name) > 1 ? `${r.name} · ${method.toUpperCase()} ${path}` : name;
  });
}

function resolveOpenApi3Server(servers) {
  if (!Array.isArray(servers) || servers.length === 0) return '';
  const url = servers[0].url || '';
  return url.replace(/\/$/, '');
}

function resolveSwagger2Base(spec) {
  const scheme =
    (Array.isArray(spec.schemes) && spec.schemes[0]) || 'https';
  const host = spec.host || '';
  const basePath = (spec.basePath || '').replace(/\/$/, '');
  if (!host) return basePath;
  return `${scheme}://${host}${basePath}`.replace(/\/$/, '');
}

function openApi3Parameters(parameters, warnings) {
  const headers = [];
  const params = [];
  for (const p of parameters || []) {
    const loc = p.in;
    const name = p.name;
    if (!name) continue;
    const value = p.example != null ? String(p.example) : '';
    const enabled = p.required !== false;
    if (loc === 'header') {
      headers.push({ key: name, value, enabled });
    } else if (loc === 'query') {
      params.push({ key: name, value, enabled });
    } else if (loc === 'cookie') {
      warnings.push(`Cookie param "${name}" skipped (use Cookies tab manually)`);
    }
  }
  return { headers, params };
}

function swagger2Parameters(parameters, warnings) {
  return openApi3Parameters(parameters, warnings);
}

function openApi3RequestBody(requestBody, warnings) {
  if (!requestBody?.content) return { body: '', body_type: 'json' };
  const jsonContent =
    requestBody.content['application/json'] ||
    requestBody.content['application/*+json'];
  if (jsonContent?.schema) {
    return {
      body: schemaExampleFromJsonSchema(jsonContent.schema),
      body_type: 'json',
    };
  }
  const formContent = requestBody.content['application/x-www-form-urlencoded'];
  if (formContent?.schema) {
    return {
      body: schemaExampleFromJsonSchema(formContent.schema),
      body_type: 'form',
    };
  }
  warnings.push('Unsupported request body content type in OpenAPI');
  return { body: '', body_type: 'raw' };
}

function swagger2Body(operation, spec, warnings) {
  const consumes = operation.consumes || spec.consumes || ['application/json'];
  const bodyParam = (operation.parameters || []).find((p) => p.in === 'body');
  if (!bodyParam?.schema) return { body: '', body_type: 'json' };
  if (consumes.some((c) => c.includes('json'))) {
    return {
      body: schemaExampleFromJsonSchema(bodyParam.schema),
      body_type: 'json',
    };
  }
  warnings.push('Swagger body imported as raw');
  return {
    body: schemaExampleFromJsonSchema(bodyParam.schema),
    body_type: 'raw',
  };
}

/**
 * One folder per OpenAPI / Swagger tag (13.09.2026 — the importer used to read
 * no `tags` at all, so every spec landed flat).
 *
 * * An operation goes into the folder of its FIRST tag. OpenAPI allows several
 *   tags per operation, but a request lives in exactly one folder, and
 *   duplicating it per tag would make two copies that drift apart.
 * * Folder order follows the spec's top-level `tags` array (the author's
 *   intended order), then any tag only used on operations, in first-seen order.
 * * Untagged operations stay at the collection root, and a spec with no tags at
 *   all produces no folders — exactly the old shape.
 */
function tagFolders(spec, operationTags) {
  const order = [];
  const seen = new Set();
  const add = (name) => {
    if (typeof name !== 'string') return;
    const trimmed = name.trim();
    if (!trimmed || seen.has(trimmed)) return;
    seen.add(trimmed);
    order.push(trimmed);
  };
  const declared = Array.isArray(spec.tags) ? spec.tags : [];
  const used = new Set(operationTags);
  // Declared tags first, but only ones an operation actually uses: an empty
  // folder for a tag nothing references is noise in the sidebar.
  for (const t of declared) if (used.has(typeof t?.name === 'string' ? t.name.trim() : '')) add(t.name);
  for (const t of operationTags) add(t);
  return order.map((name, i) => ({ id: generateFolderId(), name, parent_id: null, sort_order: i }));
}

function firstTag(operation) {
  const tag = Array.isArray(operation?.tags) ? operation.tags.find((t) => typeof t === 'string' && t.trim()) : null;
  return tag ? tag.trim() : null;
}

function buildRequestsFromPaths(paths, baseUrl, paramMapper, bodyMapper, warnings) {
  const requests = [];
  // Where each request came from, parallel to `requests` — only needed to make
  // duplicate summaries distinguishable, so it stays out of the request itself.
  const origins = [];
  for (const [path, pathItem] of Object.entries(paths || {})) {
    if (!pathItem || typeof pathItem !== 'object') continue;

    const sharedParams = pathItem.parameters || [];

    for (const method of METHODS) {
      const operation = pathItem[method];
      if (!operation) continue;

      const allParams = [...sharedParams, ...(operation.parameters || [])];
      const { headers, params } = paramMapper(allParams, warnings);
      const { body, body_type } = bodyMapper(operation, warnings);

      const fullUrl = `${baseUrl}${path}`.replace(/([^:]\/)\/+/g, '$1');

      requests.push({
        // The SUMMARY is the name a person gave the operation ("List books"); the operationId is the one
        // a code generator wants ("listBooks"). A request list is read by people. A summary that is really
        // a paragraph stays a description.
        name: requestName(operation, method, path),
        description: operation.description || operation.summary || '',
        method: normalizeMethod(method),
        url: fullUrl,
        headers,
        params,
        cookies: [],
        body,
        body_type,
        auth: defaultRequestAuth(),
        sort_order: requests.length,
        tag: firstTag(operation),
      });
      origins.push({ method, path });
    }
  }
  disambiguateNames(requests, origins);
  return requests;
}

/**
 * Turns each request's `tag` into a `folder_id`, returning the folders.
 *
 * Root children (folders AND untagged requests) are merged and sorted by
 * `sort_order` (`collectionFolders.js` childrenOf), so requests are numbered
 * AFTER the folders — otherwise an untagged request early in `paths` would land
 * between two tag folders (13.09.2026 review N3). Tag names are matched
 * exactly after trimming: `Users` and `users` are two folders, as in the spec.
 */
function groupByTag(spec, requests) {
  const folders = tagFolders(spec, requests.map((r) => r.tag).filter(Boolean));
  const idByName = new Map(folders.map((f) => [f.name, f.id]));
  requests.forEach((r, i) => {
    r.folder_id = r.tag ? idByName.get(r.tag) ?? null : null;
    r.sort_order = folders.length + i;
    delete r.tag;
  });
  return folders;
}

/**
 * @param {object} spec OpenAPI 3.x document
 * @param {string} fallbackName
 * @param {string[]} warnings
 */
export function parseOpenApi3(spec, fallbackName, warnings) {
  const baseUrl = resolveOpenApi3Server(spec.servers);
  const requests = buildRequestsFromPaths(
    spec.paths,
    baseUrl,
    openApi3Parameters,
    (op, w) => openApi3RequestBody(op.requestBody, w),
    warnings
  );
  const folders = groupByTag(spec, requests);

  return [
    {
      name: spec.info?.title || fallbackName || 'OpenAPI Collection',
      description: spec.info?.description || '',
      settings: { collection_auth: defaultCollectionAuth(), folders },
      requests,
    },
  ];
}

/**
 * @param {object} spec Swagger 2.0 document
 * @param {string} fallbackName
 * @param {string[]} warnings
 */
export function parseSwagger2(spec, fallbackName, warnings) {
  const baseUrl = resolveSwagger2Base(spec);
  const requests = buildRequestsFromPaths(
    spec.paths,
    baseUrl,
    swagger2Parameters,
    (op, w) => swagger2Body(op, spec, w),
    warnings
  );
  const folders = groupByTag(spec, requests);

  return [
    {
      name: spec.info?.title || fallbackName || 'Swagger Collection',
      description: spec.info?.description || '',
      settings: { collection_auth: defaultCollectionAuth(), folders },
      requests,
    },
  ];
}
